import { useEffect, useRef, useState } from "react";
import {
  COMPLETION_ITEM_LABEL,
  type ClinicianMedicalEvidence,
  type EvidenceSummary,
  type LowBmiAssessment,
} from "@care-by-ella/contracts";
import {
  fetchEvidenceSummary,
  fetchMedicalEvidence,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { fetchLowBmiAssessment } from "@/lib/low-bmi-api";
import {
  recommendedCompletions,
  type RecommendedCompletion,
} from "@/lib/recommended-completions";

// TES-1330 (TES-1327 §7) — the second block above the meeting note, apart
// from the evidence that has come in: "Rekommenderade kompletteringar att ta
// ställning till". The rules live in lib/recommended-completions; this only
// reads the evidence and shows the result. Nothing here sends, ticks or
// creates anything: the doctor requests a komplettering in the picker under
// "Beslut", and the meeting view hands the same list to that picker so the
// options carry the note.

// A read that fails is shown as unknown, never as "nothing to recommend".
async function settle<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (e) {
    if (e instanceof UnauthorizedError) throw e;
    return null;
  }
}

export function RecommendedCompletions({
  patientId,
  reloadKey,
  voluntary,
  onRecommendations,
  onUnauthorized,
}: {
  patientId: string;
  /** Bumped after a publish, so an item just requested drops out. */
  reloadKey: number;
  /** TES-1228 — a voluntary meeting has no picker; the overview's does it. */
  voluntary: boolean;
  onRecommendations: (recs: RecommendedCompletion[]) => void;
  onUnauthorized: () => void;
}) {
  const [recs, setRecs] = useState<RecommendedCompletion[] | null>(null);
  const [partial, setPartial] = useState(false);
  const onRecsRef = useRef(onRecommendations);
  onRecsRef.current = onRecommendations;
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    Promise.all([
      settle<EvidenceSummary>(fetchEvidenceSummary(patientId)),
      settle<LowBmiAssessment>(fetchLowBmiAssessment(patientId)),
      settle<ClinicianMedicalEvidence>(fetchMedicalEvidence(patientId)),
    ])
      .then(([evidence, lowBmi, medicalEvidence]) => {
        if (!live) return;
        const next = recommendedCompletions({
          evidence,
          lowBmi,
          medicalEvidence,
        });
        setPartial(
          evidence === null || lowBmi === null || medicalEvidence === null,
        );
        setRecs(next);
        onRecsRef.current(next);
      })
      .catch((e) => {
        if (live && e instanceof UnauthorizedError) onUnauthorizedRef.current();
      });
    return () => {
      live = false;
    };
  }, [patientId, reloadKey]);

  return (
    <section
      aria-labelledby="recommended-completions-heading"
      className="rounded-lg bg-surface p-md shadow-[0_1px_2px_rgba(42,26,40,0.06),0_8px_24px_-16px_rgba(97,32,95,0.25)]"
      data-testid="recommended-completions"
    >
      <h2
        id="recommended-completions-heading"
        className="font-display text-[18px] leading-7 text-textPrimary"
      >
        Rekommenderade kompletteringar att ta ställning till
      </h2>
      {recs === null ? (
        <p className="mt-xs text-sm text-textSecondary">Hämtar…</p>
      ) : recs.length === 0 ? (
        <p className="mt-xs text-sm text-textSecondary">
          Inga rekommenderade kompletteringar utifrån de beslutade reglerna.
        </p>
      ) : (
        <ul className="mt-xs flex flex-col gap-xs">
          {recs.map((r) => (
            <li
              key={r.key}
              className="rounded-md bg-warning/10 px-sm py-xs text-sm"
            >
              <span className="font-semibold text-textPrimary">{r.label}</span>
              <span className="text-textSecondary"> · {r.reason}</span>
              <span className="block text-xs text-textSecondary">
                {voluntary
                  ? `Begärs med ”Begär komplettering” på översikten, valet ”${COMPLETION_ITEM_LABEL[r.pickerItem]}”.`
                  : `Begärs under Beslut med valet ”${COMPLETION_ITEM_LABEL[r.pickerItem]}”.`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {partial ? (
        <p className="mt-xs text-xs text-textSecondary">
          Allt underlag kunde inte hämtas – rekommendationerna kan vara
          ofullständiga.
        </p>
      ) : null}
      <p className="mt-xs text-xs text-textSecondary">
        Stöd under samtalet. Inget skickas till patienten förrän du själv väljer
        kompletteringar och bekräftar.
      </p>
    </section>
  );
}
